import { useState } from 'react';
import type { SolTemporalKpiSnapshot } from '@economi/domain-contracts';
import { formatSolesFromCents } from '../../services/solImpactEngine';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import styles from './TrajectoryChart.module.css';

interface TrajectoryChartProps {
  readonly snapshot: SolTemporalKpiSnapshot;
}

export function TrajectoryChart({ snapshot }: TrajectoryChartProps) {
  const [hoveredDay, setHoveredDay] = useState<number>(snapshot.elapsedDays);

  const width = 760;
  const height = 240;
  const padLeft = 64;
  const padRight = 24;
  const padTop = 20;
  const padBottom = 34;

  const plotWidth = width - padLeft - padRight;
  const plotHeight = height - padTop - padBottom;

  const maxY = Math.max(
    10000,
    snapshot.effectiveSpendingCapCents,
    snapshot.totalSpentCents
  );

  const points = snapshot.dailyTrajectory;
  const totalDays = Math.max(1, points.length);

  const xForDay = (dayNumber: number): number => {
    if (totalDays <= 1) return padLeft;
    return padLeft + ((dayNumber - 1) / (totalDays - 1)) * plotWidth;
  };

  const yForCents = (cents: number): number => {
    const ratio = Math.max(0, Math.min(1.12, cents / maxY));
    return padTop + plotHeight - ratio * plotHeight;
  };

  const idealPolyline = points
    .map((pt) => `${xForDay(pt.dayNumber).toFixed(1)},${yForCents(pt.idealCumulativeCapCents).toFixed(1)}`)
    .join(' ');

  const actualPoints = points.filter((pt) => pt.actualCumulativeSpentCents !== null);
  const actualPolyline = actualPoints
    .map(
      (pt) =>
        `${xForDay(pt.dayNumber).toFixed(1)},${yForCents(pt.actualCumulativeSpentCents ?? 0).toFixed(1)}`
    )
    .join(' ');

  const firstActual = actualPoints[0];
  const lastActual = actualPoints[actualPoints.length - 1];
  const areaPath =
    firstActual && lastActual
      ? `M ${xForDay(firstActual.dayNumber).toFixed(1)} ${(padTop + plotHeight).toFixed(1)} L ${actualPoints
          .map(
            (pt) =>
              `${xForDay(pt.dayNumber).toFixed(1)} ${yForCents(pt.actualCumulativeSpentCents ?? 0).toFixed(1)}`
          )
          .join(' L ')} L ${xForDay(lastActual.dayNumber).toFixed(1)} ${(padTop + plotHeight).toFixed(1)} Z`
      : '';

  const capLineY = yForCents(snapshot.effectiveSpendingCapCents);
  const inspectedPoint =
    points.find((p) => p.dayNumber === hoveredDay) ??
    points[Math.max(0, snapshot.elapsedDays - 1)] ??
    points[0];

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem' }}>
            <h3 className={styles.title}>Trayectoria de Gasto vs. Ritmo Ideal</h3>
            <InfoPopover title="Gráfico de Trayectoria Diaria">
              Pasa el cursor o toca cualquier día del ciclo ({snapshot.startDateIso} →{' '}
              {snapshot.renewalDateIso}) para comparar tu gasto acumulado real frente al límite lineal ideal (D₀ × t).
            </InfoPopover>
          </div>
        </div>

        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={styles.swatchActual} />
            Real
          </span>
          <span className={styles.legendItem}>
            <span className={styles.swatchIdeal} />
            Ideal (D₀ × t)
          </span>
          <span className={styles.legendItem}>
            <span className={styles.swatchCap} />
            Tope ({formatSolesFromCents(snapshot.effectiveSpendingCapCents)})
          </span>
        </div>
      </div>

      <div className={styles.chartWrap}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className={styles.svgChart}
          role="img"
          aria-label="Gráfico de trayectoria de gasto acumulado frente al límite lineal"
        >
          <defs>
            <linearGradient id="actualSpendGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#006a61" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#006a61" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Líneas horizontales de guía */}
          {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
            const valCents = Math.round(maxY * fraction);
            const yPos = yForCents(valCents);
            return (
              <g key={fraction}>
                <line
                  x1={padLeft}
                  y1={yPos}
                  x2={width - padRight}
                  y2={yPos}
                  stroke="#dce9ff"
                  strokeDasharray="3 3"
                />
                <text
                  x={padLeft - 8}
                  y={yPos + 4}
                  textAnchor="end"
                  fill="#444651"
                  fontSize="10"
                  fontFamily="JetBrains Mono, monospace"
                >
                  S/ {Math.round(valCents / 100)}
                </text>
              </g>
            );
          })}

          {/* Línea de Tope de Gasto del Ciclo */}
          <line
            x1={padLeft}
            y1={capLineY}
            x2={width - padRight}
            y2={capLineY}
            stroke="#ba1a1a"
            strokeWidth="1.5"
            strokeDasharray="6 4"
          />

          {/* Área sombreada bajo la curva de gasto real */}
          {areaPath && <path d={areaPath} fill="url(#actualSpendGrad)" />}

          {/* Polilínea de trayectoria lineal ideal */}
          {idealPolyline && (
            <polyline
              fill="none"
              stroke="#00236f"
              strokeWidth="2"
              strokeDasharray="5 4"
              points={idealPolyline}
            />
          )}

          {/* Polilínea de gasto real acumulado */}
          {actualPolyline && (
            <polyline
              fill="none"
              stroke="#006a61"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              points={actualPolyline}
            />
          )}

          {/* Puntos interactivos por día */}
          {points.map((pt) => {
            const cx = xForDay(pt.dayNumber);
            const isSelected = pt.dayNumber === inspectedPoint?.dayNumber;
            const cy =
              pt.actualCumulativeSpentCents !== null
                ? yForCents(pt.actualCumulativeSpentCents)
                : yForCents(pt.idealCumulativeCapCents);

            return (
              <g
                key={pt.dayNumber}
                onMouseEnter={() => setHoveredDay(pt.dayNumber)}
                onClick={() => setHoveredDay(pt.dayNumber)}
                style={{ cursor: 'pointer' }}
              >
                {isSelected && (
                  <line
                    x1={cx}
                    y1={padTop}
                    x2={cx}
                    y2={padTop + plotHeight}
                    stroke="rgba(0, 35, 111, 0.35)"
                    strokeWidth="1.5"
                  />
                )}
                <circle
                  cx={cx}
                  cy={cy}
                  r={isSelected ? 5.5 : pt.actualCumulativeSpentCents !== null ? 3.2 : 2}
                  fill={
                    pt.actualCumulativeSpentCents !== null ? '#006a61' : '#00236f'
                  }
                  stroke="#ffffff"
                  strokeWidth="1.5"
                />
                {(pt.dayNumber === 1 ||
                  pt.dayNumber === snapshot.elapsedDays ||
                  pt.dayNumber === totalDays ||
                  pt.dayNumber % 5 === 0) && (
                  <text
                    x={cx}
                    y={height - 10}
                    textAnchor="middle"
                    fill={
                      pt.dayNumber === snapshot.elapsedDays ? '#00236f' : '#444651'
                    }
                    fontSize="10"
                    fontFamily="JetBrains Mono, monospace"
                  >
                    D{pt.dayNumber}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      {inspectedPoint && (
        <div className={styles.inspectorBar}>
          <div className={styles.inspectorCell}>
            <span className={styles.inspectorLabel}>Fecha Inspeccionada</span>
            <span className={styles.inspectorValue}>
              Día {inspectedPoint.dayNumber} ({inspectedPoint.dateIso})
            </span>
          </div>
          <div className={styles.inspectorCell}>
            <span className={styles.inspectorLabel}>Presupuesto Lineal Ideal</span>
            <span className={styles.inspectorValue}>
              {formatSolesFromCents(inspectedPoint.idealCumulativeCapCents)}
            </span>
          </div>
          <div className={styles.inspectorCell}>
            <span className={styles.inspectorLabel}>Gasto Acumulado Real</span>
            <span className={styles.inspectorValue}>
              {inspectedPoint.actualCumulativeSpentCents !== null
                ? formatSolesFromCents(inspectedPoint.actualCumulativeSpentCents)
                : 'Proyección futura'}
            </span>
          </div>
          <div className={styles.inspectorCell}>
            <span className={styles.inspectorLabel}>Cuota Segura desde ese Día</span>
            <span className={styles.inspectorValue}>
              {formatSolesFromCents(inspectedPoint.dynamicSafeAllowanceOnDayCents)}/día
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
